import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Alert, Stack, MenuItem, Select, InputLabel, FormControl, IconButton } from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem, GridToolbar } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import PageHeader from '../components/PageHeader';
import CustomNoRowsOverlay from '../components/CustomNoRowsOverlay';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import Papa from 'papaparse';
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

interface School {
  id: number;
  name: string;
  address?: string;
  contact_email?: string;
  contact_phone?: string;
  logo_url?: string;
  attendance_mode: 'session' | 'day';
  admins?: AdminUser[];
}

interface AdminUser {
  id: number;
  username: string;
  email?: string;
}

const SchoolManagement: React.FC = () => {
  const { user } = useAuth();
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [schoolName, setSchoolName] = useState('');
  const [actionError, setActionError] = useState('');
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminActionError, setAdminActionError] = useState('');
  const [adminActionSuccess, setAdminActionSuccess] = useState('');
  const [adminSchool, setAdminSchool] = useState<School | null>(null);
  const [attendanceMode, setAttendanceMode] = useState<'session' | 'day'>('session');
  const [schoolAddress, setSchoolAddress] = useState('');
  const [schoolEmail, setSchoolEmail] = useState('');
  const [schoolPhone, setSchoolPhone] = useState('');
  const [schoolLogoUrl, setSchoolLogoUrl] = useState('');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSummary, setImportSummary] = useState<{ success: number; failed: number; failedRows: any[] }>({ success: 0, failed: 0, failedRows: [] });
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [adminToAdd, setAdminToAdd] = useState('');
  const [adminAddError, setAdminAddError] = useState('');
  const [adminRemoveConfirm, setAdminRemoveConfirm] = useState<{ open: boolean; admin: AdminUser | null }>({ open: false, admin: null });
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAttendance, setFilterAttendance] = useState<string>('');
  const [adminMgmtDialogOpen, setAdminMgmtDialogOpen] = useState(false);
  const [adminMgmtSchool, setAdminMgmtSchool] = useState<School | null>(null);
  const [adminMgmtError, setAdminMgmtError] = useState('');
  const [adminMgmtAdd, setAdminMgmtAdd] = useState('');
  const [adminMgmtRemoveConfirm, setAdminMgmtRemoveConfirm] = useState<AdminUser | null>(null);
  const [adminMgmtAdmins, setAdminMgmtAdmins] = useState<AdminUser[]>([]);

  const fetchSchools = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/schools/');
      setSchools(res.data as any[]);
    } catch (err: any) {
      setError('Failed to load schools');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchoolAdmins = async (schoolId: number) => {
    try {
      const res = await api.get(`/schools/${schoolId}/admins`);
      setAdminMgmtAdmins(res.data as any[]);
    } catch (err) {
      setAdminMgmtAdmins([]);
    }
  };

  useEffect(() => {
    if (user?.role === 'superuser') {
      fetchSchools();
    }
  }, [user]);

  const handleAdd = async () => {
    setActionError('');
    try {
      await api.post('/schools', {
        name: schoolName,
        address: schoolAddress,
        contact_email: schoolEmail,
        contact_phone: schoolPhone,
        logo_url: schoolLogoUrl,
        attendance_mode: attendanceMode,
      });
      setAddDialogOpen(false);
      setSchoolName('');
      setSchoolAddress('');
      setSchoolEmail('');
      setSchoolPhone('');
      setSchoolLogoUrl('');
      fetchSchools();
    } catch (err: any) {
      setActionError(err?.response?.data?.detail || 'Failed to add school');
    }
  };

  const handleEdit = async () => {
    if (!selectedSchool) return;
    setActionError('');
    try {
      await api.patch(`/schools/${selectedSchool.id}`, {
        name: schoolName,
        address: schoolAddress,
        contact_email: schoolEmail,
        contact_phone: schoolPhone,
        logo_url: schoolLogoUrl,
        attendance_mode: attendanceMode,
      });
      setEditDialogOpen(false);
      setSelectedSchool(null);
      setSchoolName('');
      setSchoolAddress('');
      setSchoolEmail('');
      setSchoolPhone('');
      setSchoolLogoUrl('');
      fetchSchools();
    } catch (err: any) {
      setActionError(err?.response?.data?.detail || 'Failed to update school');
    }
  };

  const handleDelete = async (id: number) => {
    setActionError('');
    try {
      await api.delete(`/schools/${id}`);
      fetchSchools();
    } catch (err: any) {
      setActionError('Failed to delete school');
    }
  };

  const handleAddAdmin = async () => {
    setAdminActionError('');
    setAdminActionSuccess('');
    if (!adminSchool) return;
    try {
      await api.post('/users', {
        username: adminUsername,
        password: adminPassword,
        role: 'admin',
        school_id: adminSchool.id,
      });
      setAdminActionSuccess('Admin created successfully!');
      setAdminUsername('');
      setAdminPassword('');
      setAdminDialogOpen(false);
    } catch (err: any) {
      setAdminActionError(err?.response?.data?.detail || 'Failed to create admin');
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'name', headerName: 'School Name', flex: 1, minWidth: 200 },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 220,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => {
            setSelectedSchool(params.row);
            setSchoolName(params.row.name);
            setAttendanceMode(params.row.attendance_mode || 'session');
            setSchoolAddress(params.row.address || '');
            setSchoolEmail(params.row.contact_email || '');
            setSchoolPhone(params.row.contact_phone || '');
            setSchoolLogoUrl(params.row.logo_url || '');
            setEditDialogOpen(true);
          }}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => setDeleteConfirm({ open: true, id: params.row.id })}
        />,
        <GridActionsCellItem
          icon={<PeopleIcon />}
          label="Manage Admins"
          onClick={() => {
            setAdminMgmtSchool(params.row);
            setAdminMgmtDialogOpen(true);
            setAdminMgmtError('');
            setAdminMgmtAdd('');
            fetchSchoolAdmins(params.row.id);
          }}
          showInMenu
        />,
      ],
    },
  ];

  const handleExportCSV = () => {
    const csv = Papa.unparse(schools.map(({ admins, ...rest }) => rest));
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'schools.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadTemplate = () => {
    const headers = [
      'name',
      'address',
      'contact_email',
      'contact_phone',
      'logo_url',
      'attendance_mode'
    ];
    const sampleRow = {
      name: 'Sample School',
      address: '123 Main St',
      contact_email: 'info@school.com',
      contact_phone: '+1234567890',
      logo_url: 'https://example.com/logo.png',
      attendance_mode: 'session'
    };
    const csv = Papa.unparse({
      fields: headers,
      data: [sampleRow]
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'schools_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportCSV = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const data = results.data as any[];
        const failedRows: any[] = [];
        let success = 0;
        for (const school of data) {
          const missingFields = ['name', 'attendance_mode'].filter(f => !school[f] || school[f].trim() === '');
          if (missingFields.length > 0) {
            failedRows.push({ ...school, error: `Missing required: ${missingFields.join(', ')}` });
            continue;
          }
          if (school.attendance_mode && !['session', 'day'].includes(school.attendance_mode)) {
            failedRows.push({ ...school, error: `Invalid attendance_mode: ${school.attendance_mode}` });
            continue;
          }
          try {
            await api.post('/schools', school);
            success++;
          } catch (err: any) {
            failedRows.push({ ...school, error: err?.response?.data?.detail || 'API error' });
          }
        }
        setImportSummary({ success, failed: failedRows.length, failedRows });
        fetchSchools();
        setImportDialogOpen(false);
      },
      error: () => setImportError('Failed to parse CSV file.'),
    });
  };

  const handleDownloadFailedRows = () => {
    if (!importSummary.failedRows.length) return;
    const csv = Papa.unparse(importSummary.failedRows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'failed_schools.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filtered schools
  const filteredSchools = schools.filter(school => {
    const matchesSearch = school.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAttendance = !filterAttendance || school.attendance_mode === filterAttendance;
    return matchesSearch && matchesAttendance;
  });

  // Add admin to school
  const handleAddAdminToSchool = async () => {
    setAdminAddError('');
    if (!selectedSchool || !adminToAdd.trim()) return;
    try {
      await api.post(`/schools/${selectedSchool.id}/add_admin`, { username: adminToAdd });
      setAdminToAdd('');
      fetchSchools();
    } catch (err: any) {
      setAdminAddError(err?.response?.data?.detail || 'Failed to add admin');
    }
  };

  // Remove admin from school
  const handleRemoveAdminFromSchool = async () => {
    if (!selectedSchool || !adminRemoveConfirm.admin) return;
    try {
      await api.post(`/schools/${selectedSchool.id}/remove_admin`, { admin_id: adminRemoveConfirm.admin.id });
      setAdminRemoveConfirm({ open: false, admin: null });
      fetchSchools();
    } catch (err: any) {
      setAdminAddError('Failed to remove admin');
    }
  };

  // Admin Management Dialog
  const handleAddAdminMgmt = async () => {
    setAdminMgmtError('');
    if (!adminMgmtSchool || !adminMgmtAdd.trim()) return;
    try {
      await api.post(`/schools/${adminMgmtSchool.id}/add_admin`, { username: adminMgmtAdd });
      setAdminMgmtAdd('');
      fetchSchools();
    } catch (err: any) {
      setAdminMgmtError(err?.response?.data?.detail || 'Failed to add admin');
    }
  };
  const handleRemoveAdminMgmt = async () => {
    if (!adminMgmtSchool || !adminMgmtRemoveConfirm) return;
    try {
      await api.post(`/schools/${adminMgmtSchool.id}/remove_admin`, { admin_id: adminMgmtRemoveConfirm.id });
      setAdminMgmtRemoveConfirm(null);
      fetchSchools();
    } catch (err: any) {
      setAdminMgmtError('Failed to remove admin');
    }
  };

  if (user?.role !== 'superuser') {
    return <Alert severity="error">You are not authorized to view this page.</Alert>;
  }

  return (
    <Box>
      <PageHeader title="School Management">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />,
              endAdornment: searchTerm && (
                <IconButton size="small" onClick={() => setSearchTerm('')}><CloseIcon fontSize="small" /></IconButton>
              ),
              style: { background: '#fff', borderRadius: 8 },
            }}
            sx={{ minWidth: 220 }}
          />
          <FormControl sx={{ minWidth: 160 }} size="small">
            <InputLabel>Attendance Mode</InputLabel>
            <Select
              value={filterAttendance}
              label="Attendance Mode"
              onChange={e => setFilterAttendance(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="session">Session-based</MenuItem>
              <MenuItem value="day">Day-based</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)}>
            Add School
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCSV}>
            Export CSV
          </Button>
          <Button variant="outlined" startIcon={<FileUploadIcon />} onClick={() => setImportDialogOpen(true)}>
            Import CSV
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>
            Download Template
          </Button>
        </Stack>
      </PageHeader>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={filteredSchools}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          slots={{ toolbar: GridToolbar, noRowsOverlay: () => <CustomNoRowsOverlay message="No Schools Found" /> }}
          disableRowSelectionOnClick
          onRowClick={(params) => {
            setSelectedSchool(params.row);
            setDetailsDialogOpen(true);
          }}
        />
      </Box>
      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}>
        <DialogTitle>Add School</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="School Name"
            fullWidth
            value={schoolName}
            onChange={e => setSchoolName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Address"
            fullWidth
            value={schoolAddress}
            onChange={e => setSchoolAddress(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Contact Email"
            fullWidth
            value={schoolEmail}
            onChange={e => setSchoolEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Contact Phone"
            fullWidth
            value={schoolPhone}
            onChange={e => setSchoolPhone(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Logo URL"
            fullWidth
            value={schoolLogoUrl}
            onChange={e => setSchoolLogoUrl(e.target.value)}
          />
          {actionError && <Alert severity="error" sx={{ mt: 2 }}>{actionError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAdd} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit School</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="School Name"
              value={schoolName}
              onChange={e => setSchoolName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Address"
              value={schoolAddress}
              onChange={e => setSchoolAddress(e.target.value)}
              fullWidth
            />
            <TextField
              label="Contact Email"
              value={schoolEmail}
              onChange={e => setSchoolEmail(e.target.value)}
              fullWidth
            />
            <TextField
              label="Contact Phone"
              value={schoolPhone}
              onChange={e => setSchoolPhone(e.target.value)}
              fullWidth
            />
            <TextField
              label="Logo URL"
              value={schoolLogoUrl}
              onChange={e => setSchoolLogoUrl(e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Attendance Mode</InputLabel>
              <Select
                value={attendanceMode}
                label="Attendance Mode"
                onChange={e => setAttendanceMode(e.target.value as 'session' | 'day')}
              >
                <MenuItem value="session">Session-based</MenuItem>
                <MenuItem value="day">Day-based</MenuItem>
              </Select>
            </FormControl>
            {actionError && <Alert severity="error">{actionError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      {/* Add Admin Dialog */}
      <Dialog open={adminDialogOpen} onClose={() => setAdminDialogOpen(false)}>
        <DialogTitle>Add School Admin</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Username"
              value={adminUsername}
              onChange={e => setAdminUsername(e.target.value)}
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={adminPassword}
              onChange={e => setAdminPassword(e.target.value)}
              fullWidth
            />
            {adminActionError && <Alert severity="error">{adminActionError}</Alert>}
            {adminActionSuccess && <Alert severity="success">{adminActionSuccess}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddAdmin} variant="contained">Create Admin</Button>
        </DialogActions>
      </Dialog>
      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)}>
        <DialogTitle>Import Schools from CSV</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <b>Instructions:</b><br/>
            1. Download the <Button size="small" onClick={handleDownloadTemplate}>CSV Template</Button>.<br/>
            2. Fill in school data. <b>Required columns:</b> name, attendance_mode.<br/>
            3. <b>Attendance Mode</b> must be one of: session, day.<br/>
            4. Save as CSV and upload below.<br/>
          </Alert>
          <input type="file" accept=".csv" onChange={handleImportCSV} style={{ marginTop: 8 }} />
          {importError && <Alert severity="error" sx={{ mt: 2 }}>{importError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Import Summary Dialog */}
      <Dialog open={importSummary.success > 0 || importSummary.failed > 0} onClose={() => setImportSummary({ success: 0, failed: 0, failedRows: [] })}>
        <DialogTitle>Import Results</DialogTitle>
        <DialogContent>
          <Alert severity={importSummary.failed > 0 ? 'warning' : 'success'}>
            <b>{importSummary.success}</b> schools imported successfully.<br/>
            {importSummary.failed > 0 && <span><b>{importSummary.failed}</b> rows failed to import.</span>}
          </Alert>
          {importSummary.failed > 0 && (
            <Button variant="outlined" onClick={handleDownloadFailedRows} sx={{ mt: 2 }}>
              Download Failed Rows
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportSummary({ success: 0, failed: 0, failedRows: [] })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen && !!selectedSchool} onClose={() => setDetailsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>School Details</DialogTitle>
        <DialogContent>
          {selectedSchool && (
            <Box>
              <Stack spacing={1} mb={2}>
                <b>Name:</b> {selectedSchool.name}<br/>
                <b>Address:</b> {selectedSchool.address || '-'}<br/>
                <b>Contact Email:</b> {selectedSchool.contact_email || '-'}<br/>
                <b>Contact Phone:</b> {selectedSchool.contact_phone || '-'}<br/>
                <b>Logo:</b> {selectedSchool.logo_url ? <img src={selectedSchool.logo_url} alt="Logo" style={{ maxHeight: 40, verticalAlign: 'middle' }} /> : '-'}<br/>
                <b>Attendance Mode:</b> {selectedSchool.attendance_mode}<br/>
              </Stack>
              <b>Admins:</b>
              <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
                {(selectedSchool.admins || []).map(admin => (
                  <Box key={admin.id} sx={{ display: 'flex', alignItems: 'center', border: '1px solid #eee', borderRadius: 2, px: 1, py: 0.5, mr: 1, mb: 1 }}>
                    {admin.username} {admin.email && <span style={{ color: '#888', marginLeft: 4 }}>({admin.email})</span>}
                    <IconButton size="small" color="error" sx={{ ml: 1 }} onClick={() => setAdminRemoveConfirm({ open: true, admin })}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <TextField
                  size="small"
                  label="Add Admin by Username"
                  value={adminToAdd}
                  onChange={e => setAdminToAdd(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddAdminToSchool(); }}
                />
                <Button variant="contained" onClick={handleAddAdminToSchool}>Add</Button>
              </Stack>
              {adminAddError && <Alert severity="error" sx={{ mb: 1 }}>{adminAddError}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Remove Admin Confirmation Dialog */}
      <Dialog open={adminRemoveConfirm.open} onClose={() => setAdminRemoveConfirm({ open: false, admin: null })}>
        <DialogTitle>Remove Admin</DialogTitle>
        <DialogContent>Are you sure you want to remove this admin from the school?</DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminRemoveConfirm({ open: false, admin: null })}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleRemoveAdminFromSchool}>Remove</Button>
        </DialogActions>
      </Dialog>
      {/* Delete School Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, id: null })}>
        <DialogTitle>Delete School</DialogTitle>
        <DialogContent>Are you sure you want to delete this school? This action cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, id: null })}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => { if (deleteConfirm.id) handleDelete(deleteConfirm.id); setDeleteConfirm({ open: false, id: null }); }}>Delete</Button>
        </DialogActions>
      </Dialog>
      {/* Admin Management Dialog */}
      <Dialog open={adminMgmtDialogOpen && !!adminMgmtSchool} onClose={() => setAdminMgmtDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Admins for {adminMgmtSchool?.name}</DialogTitle>
        <DialogContent>
          <b>Current Admins:</b>
          <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
            {adminMgmtAdmins.map(admin => (
              <Box key={admin.id} sx={{ display: 'flex', alignItems: 'center', border: '1px solid #eee', borderRadius: 2, px: 1, py: 0.5, mr: 1, mb: 1 }}>
                {admin.username} {admin.email && <span style={{ color: '#888', marginLeft: 4 }}>({admin.email})</span>}
                <IconButton size="small" color="error" sx={{ ml: 1 }} onClick={() => setAdminMgmtRemoveConfirm(admin)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <TextField
              size="small"
              label="Add Admin by Username"
              value={adminMgmtAdd}
              onChange={e => setAdminMgmtAdd(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddAdminMgmt(); }}
            />
            <Button variant="contained" onClick={handleAddAdminMgmt}>Add</Button>
          </Stack>
          {adminMgmtError && <Alert severity="error" sx={{ mb: 1 }}>{adminMgmtError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminMgmtDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Remove Admin Confirmation Dialog */}
      <Dialog open={!!adminMgmtRemoveConfirm} onClose={() => setAdminMgmtRemoveConfirm(null)}>
        <DialogTitle>Remove Admin</DialogTitle>
        <DialogContent>Are you sure you want to remove this admin from the school?</DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminMgmtRemoveConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleRemoveAdminMgmt}>Remove</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchoolManagement; 